/**
 * Based on https://shadcn-country-dropdown.vercel.app/
 * Modified to use a generic dropdown component and improved TypeScript support
 */

'use client';
import React, { useCallback, forwardRef } from 'react';
import { Globe } from 'lucide-react';
import { CircleFlag } from 'react-circle-flags';
import { countries } from 'country-data-list';
import { Dropdown, DropdownOption } from './dropdown';

// Country interface
export interface Country {
  alpha2: string;
  alpha3: string;
  countryCallingCodes: string[];
  currencies: string[];
  emoji?: string;
  ioc: string;
  languages: string[];
  name: string;
  status: string;
}

// Dropdown props
interface CountryDropdownProps {
  options?: Country[];
  onChange?: (country: Country) => void;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  slim?: boolean;
}

const CountryDropdownComponent = (
  {
    options = countries.all
      .filter(
        (country: Country) =>
          country.emoji && country.status !== 'deleted' && country.ioc !== 'PRK'
      )
      .sort((a, b) => {
        // Put US and Canada first
        if (a.alpha2 === 'US') return -1;
        if (b.alpha2 === 'US') return 1;
        if (a.alpha2 === 'CA') return -1;
        if (b.alpha2 === 'CA') return 1;
        // Sort the rest alphabetically
        return a.name.localeCompare(b.name);
      }),
    onChange,
    defaultValue,
    disabled = false,
    placeholder = 'Select a country',
    slim = false,
    ...props
  }: CountryDropdownProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const dropdownOptions: DropdownOption<Country>[] = options.map(country => ({
    label: country.name,
    value: country.alpha3,
    data: country,
  }));

  const handleChange = useCallback(
    (value: string) => {
      const selectedCountry = options.find(country => country.alpha3 === value);
      if (selectedCountry) {
        onChange?.(selectedCountry);
      }
    },
    [onChange, options]
  );

  const renderOption = (option: DropdownOption<Country>) => {
    if (!option.data) return null;
    const country = option.data;
    return (
      <div className="flex items-center flex-grow w-0 gap-2 overflow-hidden">
        <div className="inline-flex items-center justify-center w-4 h-4 shrink-0 overflow-hidden rounded-full">
          <CircleFlag countryCode={country.alpha2.toLowerCase()} />
        </div>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap px-1">
          {option.label}
        </span>
      </div>
    );
  };

  const renderTrigger = (selected?: DropdownOption<Country>) => {
    if (selected?.data) {
      const country = selected.data;
      return (
        <div className="flex items-center flex-grow w-0 gap-2 overflow-hidden">
          <div className="inline-flex items-center justify-center w-4 h-4 shrink-0 overflow-hidden rounded-full">
            <CircleFlag countryCode={country.alpha2.toLowerCase()} />
          </div>
          {!slim && (
            <span className="overflow-hidden text-ellipsis whitespace-nowrap px-3">
              {selected.label}
            </span>
          )}
        </div>
      );
    }
    return <span>{!slim ? placeholder : <Globe size={20} />}</span>;
  };

  return (
    <Dropdown<Country>
      ref={ref}
      options={dropdownOptions}
      value={defaultValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      slim={slim}
      renderOption={renderOption}
      renderTrigger={renderTrigger}
      searchPlaceholder="Search country..."
      showSearch={true}
      {...props}
    />
  );
};

CountryDropdownComponent.displayName = 'CountryDropdownComponent';

export const CountryDropdown = forwardRef(CountryDropdownComponent);
